import { createBybitUrl, domain, replacePath, replaceVar } from '@web3/utils';
import { latestStakingStore } from '../../../store/stakingTask';
import { checkSupport, subAppPaths } from '../../../utils/constants';
import { ANNOUNCEMENT_LINK, DEVELOPER_LINK, RELEASE_NOTES_LINK } from '../../../utils/url';
import { useLocale } from '../../../store/i18nMessage';
import { isSupportCountry } from '../../../utils/regulationCountry';

/** 子菜单 */
export type SubMenu = {
  /** 标识 */
  symbol: string;
  /** 链接文案 */
  title: string;
  /** 图标 */
  icon?: string;
  /** 链接地址 */
  link: string;
  /** 尺寸（子菜单） */
  size?: 'large';
  hot?: boolean;
  /** 是否为新业务 */
  isNew?: boolean;
  /** 标签 */
  tag?: {
    label: string;
    type: string;
  };
  /** 描述 */
  description?: string;
  /** 是否在新页面打开 */
  isBlank?: boolean;
  /** 是否为子应用 */
  isChildApp?: boolean;
};

/** 菜单 */
export type Menu = {
  /** 标识 */
  symbol: string;
  /** 链接文案 */
  title: string;
  /** logo */
  icon?: string;
  /** 描述 */
  description?: string;
  /** 链接地址 */
  link: string;
  /** 是否显示小火苗图标（主菜单） */
  hot?: boolean;
  /** 是否为新业务 */
  isNew?: boolean;
  /** 控制显示 */
  control?: () => boolean;
  /** 子菜单标题 */
  subTitle?: string;
  /** 子菜单 */
  children?: SubMenu[];
};

export const useMenuList = () => {
  const { activity } = latestStakingStore.useStoreState();
  const { t, locale } = useLocale();

  const defaultMenus = [
    {
      link: replacePath('/web3/home'),
      title: t('home_page'),
      symbol: 'Home',
    },
    {
      link: '',
      title: t('earn'),
      symbol: 'Earn',
      children: [
        {
          hidden: !checkSupport(locale, 'airdrop'),
          link: replacePath('/web3/airdrop'),
          title: t('airdropArcade'),
          symbol: 'AirdropArcade',
          hot: true,
          description: t('reward_banner_desc'),
        },
        // {
        //   hidden: !checkSupport(locale, 'ido'),
        //   link: replacePath('/web3/ido'),
        //   title: t('IDO'),
        //   symbol: 'IDO',
        //   hot: true,
        //   description: t('exploreSupportProjects'),
        // },
        {
          link: replacePath('/web3/staking'),
          hot: (activity?.endTime ?? 0) > Date.now(),
          title: t('staking'),
          symbol: 'Staking',
          description: t('earnPassiveIncome'),
        },
        {
          link: replacePath('/web3/staking/BybitSOL'),
          hot: false,
          title: 'Bybit Staked SOL（bbSOL）',
          symbol: 'BybitSOL',
          description: t('firstExchangeBackedLst1'),
        },
        {
          link: replacePath('/web3/points'),
          title: t('bybitWeb3Points'),
          symbol: 'Points',
          description: t('earnWeb3PointsByExploring'),
        },
      ].filter((v) => !v.hidden),
    },

    // {
    //   link: '',
    //   title: t('tradeNav'),
    //   symbol: 'Trade',
    //   children: [
    //     {
    //       // 等本项目上线的时候，应该不需要灰度了，估计直接展示
    //       //   hidden: !userStore.showDexEntry,
    //       link: replacePath('/web3/dex/?utm_content=web3Menu_dexPro'),
    //       title: t('dexPro'),
    //       description: t('smartDexTradingAnalytics'),
    //       isChildApp: true,
    //       hot: true,
    //       symbol: 'dexPro_home',
    //     },
    //     {
    //       link: replacePath('/web3/swap/market'),
    //       title: t('navMarketTitle'),
    //       description: t('navMarketDesc'),
    //       symbol: 'SwapMarketing',
    //     },
    //     {
    //       link: replacePath('/web3/swap-bridge'),
    //       title: t('swapAggregator'),
    //       description: t('aggregateDexMultiChains'),
    //       symbol: 'SwapAggregator',
    //     },
    //     {
    //       link: replacePath('/web3/buy-crypto'),
    //       title: t('nav_crypto'),
    //       description: t('buyCryptoAnytimeAnywhereEase'),
    //       symbol: 'BuyCrypto',
    //     },
    //   ],
    // },
    {
      link: '',
      title: t('marketPlace'),
      symbol: 'MarketPlace',
      children: [
        // {
        //   link: replacePath('/web3/inscription'),
        //   title: t('inscriptions'),
        //   description: t('inscriptionsDesc'),
        //   symbol: 'Inscription',
        //   isNew: true,
        //   isChildApp: true,
        // },
        // {
        //   link: replacePath('/web3/nft-pro'),
        //   title: 'NFT Pro',
        //   description: t('nxtGenNFTMarketplace'),
        //   symbol: 'NFTPro',
        //   isChildApp: true,
        // },
        {
          hidden: !checkSupport(locale, 'nft'),
          link: replacePath('/nft'),
          title: 'NFT',
          description: t('exploreNftsWorldwide'),
          symbol: 'NFT',
          isBlank: true,
        },
      ].filter((v) => !v.hidden),
    },
    {
      link: '',
      title: t('explore'),
      symbol: 'Explore',
      children: [
        {
          link: createBybitUrl(replaceVar(ANNOUNCEMENT_LINK, { locale: locale })),
          title: t('web3Announcements'),
          description: t('findLatestUpdates'),
          symbol: 'Web3Announcement',
          isBlank: true,
        },
        {
          link: `https://learn.${domain}/web3/`,
          title: t('web3Learn'),
          description: t('learnAboutWeb3'),
          symbol: 'Web3Learn',
          isBlank: true,
        },
        {
          link: replacePath(subAppPaths['crypto-ark']),
          title: t('theCryptoArk'),
          symbol: 'TheCryptoArk',
          description: t('exploreWeb3'),
          isChildApp: true,
        },
      ],
    },
    {
      link: '',
      title: t('build'),
      symbol: 'Build',
      children: [
        {
          link: DEVELOPER_LINK,
          title: t('bybitWalletInteg'),
          description: t('integrateBybitWallet'),
          symbol: 'BybitWalletIntegration',
          isBlank: true,
        },
        {
          link: RELEASE_NOTES_LINK,
          title: t('versionUpdateHistory'),
          description: t('viewDetailsForUpdate'),
          symbol: 'BybitWalletIntegrationChangeLog',
          isBlank: true,
        },
      ],
    },
    {
      link: replacePath('/web3/aidol2025/?utm_content=web3Menu_aidol'),
      title: t('aiDolSuperstar'),
      symbol: 'AIDOL2025',
    },
  ].filter((m) => m.children?.length || m.link);

  const getMenus = async () => {
    // 获取当前用户合规信息是否NZL
    const isNZL = await isSupportCountry('NZL');

    if (isNZL) {
      // 删除Apex链接
      return defaultMenus.map((menu) => {
        if (menu.symbol === 'Trade' && menu.children) {
          menu.children = menu.children?.filter((child) => child.symbol !== 'Apex') ?? [];
        }
        return menu;
      });
    } else {
      return defaultMenus;
    }
  };

  return {
    getMenus,
    defaultMenus,
  };
};
